// src/Components/Report/ExpenseReport.js
import React, { useRef } from "react";
import {
  LinearProgress, Typography, TextField, InputAdornment, Button,
  Card, CardContent, Grid, Table, TableBody, TableCell, TableContainer, TableRow
} from "@material-ui/core";  // ← ADDED Table imports
import { Search as SearchIcon } from "@material-ui/icons";
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, 
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

import ReusableDashboardHeader from "../../Containers/Dasboard/ReusableDashboardHeader";
import { BackButton } from "../../Utils/FormInputs/BackButton";
import Label from "../../Utils/FormInputs/Label";
import { Container } from "../../Utils/styledComponents";
import { EnhancedTableHead, MuiPaper } from "../../Utils/Tables/CustomTable";
import { formatAmount } from "../../Utils/formatAmount";
import { ReusableSalesAndPurchaseHeader } from "./ReusableSalesAndPurchaseHeader";
import { useExpenseReport } from "./CustomHooks/useExpenseReport";
import { useExcelReports, usePdfReports } from "../Invoice/CustomHooks/useExcelReports";

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#ff7c7c', '#8b5cf6', '#d946ef'];

const headData = [
  { id: "date", label: "Date" },
  { id: "description", label: "Description" },
  { id: "vendor", label: "Vendor" },
  { id: "expense_head", label: "Category" },
  { id: "paid_via", label: "Paid Via" },
  { id: "memo", label: "Memo" },
  { id: "amount", label: "Amount" },
];

const ExpenseReport = (props) => {
  const {
    loading,
    report,
    from_date,
    to_date,
    search,
    setSearch,
    handleChangeDate,
    loadMore,
    hasMore
  } = useExpenseReport();

  const { actions } = props;
  const csvRef = useRef();

  const { summary = {}, expenses = [], chart_data = {} } = report;
  const {
    total_expense = 0,
    total_transactions = 0,
    average_per_transaction = 0,
    paid_by = []
  } = summary;

  const { monthly_trend = [], top_categories = [] } = chart_data;

  const csvData = expenses.map(e => ({
    Date: e.date,
    Description: e.description,
    Vendor: e.vendor,
    Category: e.expense_head,
    "Paid Via": e.paid_via,
    Memo: e.memo,
    Amount: e.amount
  }));

  const headers = [
    { key: "Date", label: "Date" },
    { key: "Description", label: "Description" },
    { key: "Vendor", label: "Vendor" },
    { key: "Category", label: "Category" },
    { key: "Paid Via", label: "Paid Via" },
    { key: "Memo", label: "Memo" },
    { key: "Amount", label: "Amount" },
  ];

  const currentObj = { 
    file_name: "Expense Report", 
    url: "Creport/expense_report_excel", 
    params: { start_date: from_date, end_date: to_date } 
  };
  const objPDF = { 
    file_name: "Expense Report", 
    url: "Creport/expense_report_pdf", 
    params: { start_date: from_date, end_date: to_date } 
  };
  
  const { exportExcel } = useExcelReports(actions, currentObj);
  const { exportPDF } = usePdfReports(actions, objPDF);

  return (
    <div className="journals">
      <ReusableDashboardHeader
        component="Expense Report"
        heading="Reports"
        subHeading="Expense Analysis & Trends"
        link="/expense-report"
      />

      <Container>
        <div className="mb-3">
          <BackButton text="All Reports" to="/allreports" />
        </div>

        <ReusableSalesAndPurchaseHeader
          handleSubmit={(e) => e.preventDefault()}
          exportExcel={exportExcel}
          exportPdf={exportPDF}
          csvData={csvData}
          fetchCsvData={() => csvRef.current?.link.click()}
          csvRef={csvRef}
          headers={headers}
          csvTitle="Expense Report"
        >
          <div className="d-flex flex-wrap align-items-end gap-3">
            <div>
              <Label name="From Date" />
              <input 
                type="date" 
                className="form-control" 
                value={from_date} 
                name="from_date" 
                onChange={handleChangeDate} 
              />
            </div>
            <div>
              <Label name="To Date" />
              <input 
                type="date" 
                className="form-control" 
                value={to_date} 
                name="to_date" 
                onChange={handleChangeDate} 
              />
            </div>
            <div style={{ minWidth: 320 }}>
              <Label name="Search (Description, Vendor, Category, Memo)" />
              <TextField
                fullWidth 
                size="small" 
                variant="outlined"
                value={search} 
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{ 
                  startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> 
                }}
                placeholder="Search expenses..."
              />
            </div>
          </div>
        </ReusableSalesAndPurchaseHeader>

        {/* Summary Cards */}
        <Grid container spacing={3} className="mt-3">
          <Grid item xs={12} md={4}>
            <Card className="shadow-sm border-start border-danger">
              <CardContent>
                <Typography color="textSecondary" gutterBottom>Total Expenses</Typography>
                <Typography variant="h4" className="text-danger fw-bold">
                  {formatAmount(total_expense)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card className="shadow-sm border-start border-primary">
              <CardContent>
                <Typography color="textSecondary" gutterBottom>Total Transactions</Typography>
                <Typography variant="h4" className="text-primary fw-bold">
                  {total_transactions.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card className="shadow-sm border-start border-success">
              <CardContent>
                <Typography color="textSecondary" gutterBottom>Avg per Transaction</Typography>
                <Typography variant="h4" className="text-success fw-bold">
                  {formatAmount(average_per_transaction)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Charts */}
        <Grid container spacing={3} className="mt-3">
          {/* Monthly Trend Chart */}
          <Grid item xs={12} lg={8}>
            <Card className="shadow-sm">
              <CardContent>
                <Typography variant="h6" gutterBottom>Monthly Expense Trend</Typography>
                {monthly_trend.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={monthly_trend}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis tickFormatter={(v) => formatAmount(v)} />
                      <Tooltip formatter={(v) => formatAmount(v)} />
                      <Line 
                        type="monotone" 
                        dataKey="amount" 
                        stroke="#d32f2f" 
                        strokeWidth={3} 
                        dot={{ fill: '#d32f2f', strokeWidth: 2 }} 
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-center py-4">
                    <Typography color="textSecondary">No monthly data available</Typography>
                  </div>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Top Categories Pie Chart */}
          <Grid item xs={12} lg={4}>
            <Card className="shadow-sm">
              <CardContent>
                <Typography variant="h6" gutterBottom>Top 10 Categories</Typography>
                {top_categories.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={top_categories}
                        cx="50%" 
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name}\n${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        dataKey="amount"
                        nameKey="category"
                      >
                        {top_categories.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={COLORS[index % COLORS.length]} 
                          />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v) => formatAmount(v)} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-center py-4">
                    <Typography color="textSecondary">No category data</Typography>
                  </div>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Expenses Table */}
        <MuiPaper className="mt-4">
          {loading && <LinearProgress />}

          <div className="p-3 bg-light border-bottom d-flex justify-content-between align-items-center">
            <Typography variant="subtitle1">
              <strong>{expenses.length}</strong> expenses shown
            </Typography>
            {hasMore && (
              <Button 
                variant="contained" 
                color="secondary" 
                onClick={loadMore} 
                disabled={loading}
                size="small"
              >
                {loading ? "Loading..." : "Load More"}
              </Button>
            )}
          </div>

          <TableContainer>
            <Table size="small">
              <EnhancedTableHead headCells={headData} />
              <TableBody>
                {expenses.length > 0 ? (
                  expenses.map((e, i) => (
                    <TableRow hover key={i}>
                      <TableCell>{e.date}</TableCell>
                      <TableCell>{e.description || "-"}</TableCell>
                      <TableCell>{e.vendor}</TableCell>
                      <TableCell>
                        <span className="badge bg-secondary">{e.expense_head}</span>
                      </TableCell>
                      <TableCell>
                        <span className={`badge ${e.paid_via === 'Cash' ? 'bg-warning text-dark' : 'bg-info'}`}>
                          {e.paid_via}
                        </span>
                      </TableCell>
                      <TableCell>{e.memo || "-"}</TableCell>
                      <TableCell align="right" className="fw-bold text-danger">
                        {formatAmount(e.amount)}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center" className="py-4">
                      <Typography variant="body1" color="textSecondary">
                        No expenses found for the selected period
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </MuiPaper>
      </Container>
    </div>
  );
};

export default ExpenseReport;